import { getAllAvailableDevicesToAddByGroupIdSelector } from '../../../redux/commander/selectors';
import { getMachineTypeSelector } from '../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { useControlUnitsByIds } from '../../../redux/controlUnit/hooks/useControlUnitsByIds';
import { getControlUnitsActiveByIdFilter } from '../../../redux/controlUnit/selectors';
import { putGroup } from '../../../redux/groups/actions/thunks';
import { ChannelMap } from '../../../redux/groups/api/group.model';
import { GroupAPI } from '../../../redux/groups/api/GroupAPI';
import { getRightUserUtilSelector } from '../../../redux/rights/selectors';
import { getMyWorkspaceIdSelector } from '../../../redux/stoerkID/selectors/StoerkId.selectors';
import { useAppDispatch, useAppSelector } from '../../../redux/store.model';
import { Merchandiser } from '../redux/composableDevice/model/Merchandiser.model';
import { getChannelsByComposableDevice } from '../util/getChannelsByComposableDevice';

export const useSetChannels = (merchandiser: Merchandiser) => {
  const dispatch = useAppDispatch();
  const myWorkspaceId = useAppSelector(getMyWorkspaceIdSelector);
  const rightsUserUtil = useAppSelector(getRightUserUtilSelector);
  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: merchandiser?.composableDevice?.machineTypeId,
    })
  );
  const canUpdateGroup = rightsUserUtil.hasRightsToUpdateGroup(
    merchandiser.id,
    myWorkspaceId
  );
  const { data: devicesControlUnits } = useControlUnitsByIds({
    deviceIds: merchandiser.devices,
  });
  const devices = useAppSelector((state) =>
    getAllAvailableDevicesToAddByGroupIdSelector(state, {
      groupId: merchandiser.parentGroupId,
    })
  );
  const { data: group } = GroupAPI.useGetGroupByIdQuery(merchandiser.id, {
    skip: !merchandiser.id,
  });
  const selectedDevice = devices.find(
    (device) => device.uuid === (merchandiser.devices?.[0] || '')
  );
  const safeDevicesControlUnits = devicesControlUnits || [];
  const deviceControlUnits = (
    safeDevicesControlUnits.find(
      (device) => device.deviceId === selectedDevice?.uuid
    )?.controlUnits || []
  ).filter(getControlUnitsActiveByIdFilter);

  const oldChannels = (group?.composableDevice?.channels || []) as ChannelMap[];
  const doesControlUnitMatchChannel = (
    channel: ChannelMap,
    controlUnit: (typeof deviceControlUnits)[number]
  ) => {
    return controlUnit.id === channel.controlUnitId;
  };

  const needToUpdateChannels =
    group &&
    canUpdateGroup &&
    oldChannels.length > 0 &&
    deviceControlUnits.length > 0 &&
    oldChannels
      .map((channel) =>
        deviceControlUnits.some((cu) =>
          doesControlUnitMatchChannel(channel, cu)
        )
      )
      .filter(Boolean).length === 0;

  const updateChannels = async () => {
    if (!canUpdateGroup || !needToUpdateChannels) {
      return;
    }

    const channelList = machineType
      ? getChannelsByComposableDevice(
          machineType,
          deviceControlUnits,
          selectedDevice?.uuid || ''
        ) || []
      : [];

    const sensorChannelsIndex =
      machineType?.labels['merchandise_channel_sensors']?.split('-') || [];
    const [, endSensorChannels] = sensorChannelsIndex.map((value) =>
      parseInt(value, 10)
    );
    const actionsChannelsIndex =
      machineType?.labels['merchandise_channel_actions']?.split('-') || [];
    const [initialActionsChannels] = actionsChannelsIndex.map((value) =>
      parseInt(value, 10)
    );
    const virtualChannelsIndex =
      machineType?.labels['merchandise_channel_virtual_sensors']?.split('-') ||
      [];
    const [, endVirtualChannels] = virtualChannelsIndex.map((value) =>
      parseInt(value, 10)
    );

    const startChannelIndex = Number.isNaN(initialActionsChannels)
      ? 0
      : initialActionsChannels;
    const endChannelIndex = Number.isNaN(endVirtualChannels)
      ? endSensorChannels
      : endVirtualChannels;
    const newChannels: ChannelMap[] = [];
    for (let i = startChannelIndex; i <= endChannelIndex; i++) {
      if (!deviceControlUnits.length) {
        continue;
      }

      newChannels.push(channelList[i]);
    }

    await dispatch(
      putGroup(merchandiser?.id ?? '', {
        ...merchandiser,
        composableDevice: {
          ...merchandiser?.composableDevice,
          channels: channelList,
        },
      })
    );
  };

  return {
    needToUpdateChannels,
    updateChannels,
  };
};
